import { XIcon } from "lucide-react";
import { useCallback, useState } from "react";
import { useIssueDonationsRefundMutation } from "../../../../state/domainRouterApi";

const PRIMARY = "#2697a2";
const TEXT = "#404f4d";
const BORDER = "#e5e7eb";
const DANGER = "#dc2626";

export function RefundModal({
  isOpen,
  onClose,
  donationId,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  donationId: string;
  onSuccess: () => void;
}) {
  const [issueRefund, { isLoading: submitting }] =
    useIssueDonationsRefundMutation();
  const [reason, setReason] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setReason("");
    setSubmitError(null);
    onClose();
  }, [onClose]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!reason.trim()) {
      setSubmitError("Please provide a reason for the refund.");
      return;
    }

    try {
      await issueRefund({
        donationId,
        reason: reason.trim(),
      }).unwrap();
      setReason("");
      onSuccess();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : err && typeof err === "object" && "message" in err
            ? String((err as { message?: unknown }).message)
            : "Failed to issue refund. The donations backend may not be deployed.";
      setSubmitError(message);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Issue refund"
    >
      <div
        data-passive-click-surface="true"
        className="fixed inset-0 bg-black/40"
        onClick={handleClose}
        onKeyDown={(event) => {
          if (event.key === "Escape") handleClose();
        }}
        role="button"
        tabIndex={-1}
        aria-label="Close modal"
      />
      <div className="relative mx-4 w-full max-w-[480px] rounded-2xl bg-white shadow-xl">
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: BORDER }}
        >
          <h2 className="text-lg font-semibold" style={{ color: TEXT }}>
            Issue Refund
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-full p-1 transition-colors hover:bg-gray-100"
            aria-label="Close"
          >
            <XIcon className="h-5 w-5" style={{ color: TEXT }} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-6">
          <p className="text-sm" style={{ color: `${TEXT}99` }}>
            This action will initiate a refund for donation{" "}
            <strong style={{ color: TEXT }}>{donationId}</strong>. This cannot
            be undone.
          </p>

          <div>
            <label
              htmlFor="refund-reason"
              className="mb-1.5 block text-sm font-medium"
              style={{ color: TEXT }}
            >
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              id="refund-reason"
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
                setSubmitError(null);
              }}
              required
              rows={4}
              className="block w-full resize-none rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:ring-2"
              style={{ borderColor: BORDER, color: TEXT }}
              placeholder="Describe why this refund is being issued..."
            />
          </div>

          {submitError ? (
            <div
              className="rounded-xl border px-4 py-3 text-sm"
              style={{
                borderColor: "#fbbf24",
                backgroundColor: "#fffbeb",
                color: "#92400e",
              }}
            >
              {submitError}
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg border px-4 py-2.5 text-sm font-medium transition-colors hover:bg-gray-50"
              style={{ borderColor: BORDER, color: TEXT }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              style={{ backgroundColor: DANGER }}
            >
              {submitting ? "Processing..." : "Confirm Refund"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
